<template>
  <layout
    class="bg-gradient-dark text-white flex justify-center"
    v-statusbar:#20303d.light
  >
    <top-small class="text-interaction-light">
      <template v-slot:left>
        <back-button returnPath="election-home" />
      </template>
      <template v-slot:default>
        <sorting-buttons
          v-show="canUseFilters"
          ref="sorting"
          class="px-10"
          v-if="viewState != null"
          :candidateIds="sortableCandidates"
          @update:candidateIds="updateSortedCandidates"
          @filterchanged="onFilterChanged"
          :initialState="viewState.filter"
          :defaultSortBy="defaultSortBy"
          :overlay-bus="sortingButtonsBus"
          :show-search-button="false"
          style="position:relative;top:50%;transform:translateY(-50%)"
        />
      </template>
      <template v-slot:right>
        <tutorial-button :show-bus="tutorialBus" />
        <template v-if="canSwitchView">
          <button
            class="mt-12 cursor-pointer text-5xl"
            @click="toggleAllCandidatesView"
          >
            <icon-swipe-candidates v-if="showAllCandidates" />
            <icon-all-candidates v-else />
          </button>
        </template>
      </template>
    </top-small>
    <template v-if="viewState != null">
      <template v-if="showAllCandidates">
        <all-candidates
          :candidates="allCandidates"
          :sortingBus="sortingButtonsBus"
          :initialState="viewState"
          @scroll="onAllCandidatesScroll"
        />
      </template>
      <template v-else-if="currentCandidateQueue.length > 0">
        <top-sub-a class="top-spacer" />
        <div class="flex-1 relative h-full" style="z-index: 1">
          <content-scroll-container :scroll="false" :overflow-visible="true">
            <content-scroll-inner class="h-full">
              <div class="relative w-full h-full">
                <vue-swing
                  @throwout="onThrowout"
                  :config="config"
                  ref="vueswing"
                  @dragmove="onDragMove"
                  @dragend="onDragEnd"
                  class="overflow-visible absolute inset-0"
                >
                  <!-- Slice to copy array, reverse since it is a stack -->
                  <template v-for="(id, index) in currentCandidateQueue">
                    <candidate-card
                      :key="id"
                      :candidate-id="id"
                      :drag-bus="dragBus"
                      v-if="index === 0"
                      class="bg-gradient"
                      @match="onMatch"
                    ></candidate-card>
                  </template>
                </vue-swing>
              </div>
            </content-scroll-inner>
          </content-scroll-container>
        </div>
      </template>
      <template v-else-if="matchableCandidates.length === 0">
        <content-scroll-container
          class="flex-1 h-full"
          :scroll="false"
          :overflow-visible="true"
        >
          <content-scroll-inner class="h-full flex flex-col justify-center">
            <p>
              {{ $t("views.main.match.snippet_1") }}
              {{ activeElection.label }}
              {{ $t("views.main.match.snippet_2") }}
            </p>
            <p>
              {{ $t("views.main.match.snippet_3") }}
            </p>
            <router-link :to="{ name: 'favorites' }">
              <navigate-small-button
                :theme="'border-white'"
                class="my-5"
                style="font-weight: normal"
              >
                {{ $t("views.main.match.button_1") }}
              </navigate-small-button>
            </router-link>
            <p>
              {{ $t("views.main.match.snippet_4") }}
            </p>
          </content-scroll-inner>
        </content-scroll-container>
      </template>
      <template v-else-if="hasActiveFilter">
        <content-scroll-container
          class="flex-1 h-full"
          :scroll="false"
          :overflow-visible="true"
        >
          <content-scroll-inner class="h-full flex flex-col justify-center">
            <p>{{ $t("views.main.match.no_candidates_with_filter") }}</p>
            <navigate-small-button
              :theme="'border-white'"
              @click="resetFilter()"
              class="my-5"
              style="font-weight: normal"
            >
              {{ $t("views.main.match.reset_filter") }}
            </navigate-small-button>
          </content-scroll-inner>
        </content-scroll-container>
      </template>
      <template v-else>
        <content-scroll-container
          class="flex-1 h-full"
          :scroll="false"
          :overflow-visible="true"
        >
          <content-scroll-inner class="h-full flex flex-col justify-center">
            <p class="my-3 font-bold">
              {{ $t("views.main.match.no_candidates") }}
            </p>
            <p class="my-3">{{ $t("views.main.match.view_favorites") }}</p>
            <router-link :to="{ name: 'favorites' }">
              <navigate-small-button
                :theme="'border-white'"
                class="my-5"
                style="font-weight: normal"
              >
                {{ $t("views.main.match.button_1") }}
              </navigate-small-button>
            </router-link>
            <p class="my-3">
              {{ $t("views.main.match.reevaluate_candidates") }}
            </p>
            <navigate-small-button
              :theme="'border-white'"
              @click="resetCandidateList()"
              class="my-5"
              style="font-weight: normal"
            >
              {{ $t("views.main.match.reevaluate") }}
            </navigate-small-button>
          </content-scroll-inner>
        </content-scroll-container>
      </template>
    </template>
    <template v-else>
      <div class="w-full h-full relative">
        <icon-loading-spinner
          :fade="true"
          class="absolute-center"
          color="gray"
        ></icon-loading-spinner>
      </div>
    </template>
    <bottom-section v-if="viewState != null">
      <template v-slot:default>
        <progress-bar
          :max="candidates.length"
          :value="matchedCandidates.length + dislikedCandidates.length"
        ></progress-bar>
      </template>
      <template v-slot:info>
        <span
          v-html="
            $t('views.main.match.progress', {
              seats: activeElection.seats,
              evaluated: matchedCandidates.length + dislikedCandidates.length,
              total: candidates.length,
              filtered: currentCandidateQueue.length
            })
          "
        ></span>
      </template>
    </bottom-section>

    <sorting-button-overlays :overlay-bus="sortingButtonsBus" />

    <tutorial :show-bus="tutorialBus" />

    <achievement link="election-home" v-if="showAchievement">
      <template v-slot:subtitle>
        {{ $t("views.main.match.achievement_1") }}
      </template>
      <template v-slot:icon>
        <img
          class="absolute top-0 right-0 bottom-0 left-0"
          src="@/assets/achievements/unlock-pro-f02-hero.png"
        />
      </template>
      <template v-slot:info-text>
        {{ $t("views.main.match.achievement_2") }}
      </template>
      <template v-slot:new-achievement-text>
        {{ $t("views.main.match.achievement_3") }}
      </template>
    </achievement>
  </layout>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import VueSwing from "vue-swing";

import store from "@/store";

import Layout from "@/components/layouts/LayoutCard";
import Tutorial from "@/components/tutorials/TutorialMatch";
import CandidateCard from "@/components/people/CandidateCard";
import SortingButtons from "@/components/buttons/SortingButtons";
import Achievement from "@/components/achievements/Achievement";
import Vue from "vue";
import ContentScrollContainer from "@/components/layouts/ContentScrollContainer";
import SortingButtonOverlays from "../../components/buttons/SortingButtonOverlays";
import TopSmall from "@/components/layouts/TopSmall";
import BackButton from "@/components/buttons/BackButton";
import TopSubA from "@/components/layouts/TopSubA";
import BottomSection from "@/components/layouts/BottomSection";
import ProgressBar from "@/components/user/ProgressBar";
import TutorialButton from "@/components/buttons/TutorialButton";
import { ProgressStepId } from "@/utils/ProgressStepId";
import { ElectionProgressStepId } from "@/utils/ElectionProgressStepId";
import NavigateSmallButton from "@/components/buttons/NavigateSmallButton";
import ContentScrollInner from "@/components/layouts/ContentScrollInner";
import { ViewStateMixin } from "@/utils/ViewStateMixin";
import { ProgressState } from "@/utils/ProgressState";
import { MatchType } from "@/utils/MatchType";
import StatusBarDirective from "@/directives/StatusBarDirective";

import AllCandidates from "@/components/people/AllCandidates";
import IconAllCandidates from "@/components/icons/IconAllCandidates.vue";
import IconSwipeCandidates from "@/components/icons/IconSwipeCandidates.vue";
import constants from "@/constants";
import IconLoadingSpinner from "@/components/icons/IconLoadingSpinner";

const viewId = "match";

export default {
  name: "Match",
  directives: {
    statusbar: StatusBarDirective
  },
  components: {
    IconLoadingSpinner,
    ContentScrollInner,
    NavigateSmallButton,
    TutorialButton,
    ProgressBar,
    BottomSection,
    TopSubA,
    BackButton,
    TopSmall,
    SortingButtonOverlays,
    ContentScrollContainer,
    Layout,
    Tutorial,
    VueSwing,
    CandidateCard,
    SortingButtons,
    Achievement,
    AllCandidates,
    IconAllCandidates,
    IconSwipeCandidates
  },
  mixins: [ViewStateMixin],
  async beforeRouteEnter(to, from, next) {
    if (store.getters["election/activeElection"] == null) {
      next({ name: "election-home" });
      return;
    }

    if (!store.getters["user/hasProgress"](ProgressStepId.Questionnaire)) {
      next({ name: "splash" });
      return;
    }

    next();
  },
  data: () => ({
    viewId: viewId,
    viewState: null,
    dragBus: new Vue(),
    candidateIds: [],
    currentCandidateQueue: [],
    config: {
      allowedDirections: [
        VueSwing.Direction.LEFT,
        VueSwing.Direction.RIGHT,
        VueSwing.Direction.UP
      ],
      isThrowOut: function(xOffset, yOffset) {
        const width = Math.min(768, window.screen.width);
        const height = Math.min(1024, window.screen.height);
        const referenceAxis = Math.min(width, height);
        const distance = Math.max(Math.abs(xOffset), Math.abs(yOffset));
        return distance > referenceAxis * 0.35;
      }
    },
    sortingButtonsBus: new Vue(),
    electionSeen: false,
    /**
     * Whether the user has matched all candidates of an election before
     */
    hasMatchedCandidates: false,
    canUseFilters: false,
    showAchievement: false,
    tutorialBus: new Vue(),
    hasActiveFilter: false,
    showAllCandidates: false,
    canSwitchView: false,
    allCandidates: []
  }),
  async mounted() {
    await Promise.all([
      store.dispatch("candidate/fetchCandidates"),
      store.dispatch("candidate/fetchMatches"),
      store.dispatch("election/fetchElections")
    ]);
    this.totalCandidates = this.candidates.length;
    this.allCandidates = this.candidates;
    this.electionSeen = this.hasElectionProgress(
      ElectionProgressStepId.Match,
      this.activeElection.id
    );
    this.hasMatchedCandidates = this.hasProgress(ProgressStepId.Match);
    this.canUseFilters = this.hasMatchedCandidates;
    this.canSwitchView = this.hasMatchedCandidates;
    this.viewState = this.getViewState(this.viewId) || {};

    this.setDefaultSortByViewState();
    if (this.viewState.showAllCandidates) {
      this.toggleAllCandidatesView();
    }

    // when the user enters this view from candidate profile, recheck whether all candidates evaluated
    this.unlockAchievement();
  },
  computed: {
    ...mapGetters("user", ["user", "hasProgress"]),
    ...mapGetters("candidate", [
      "candidate",
      "candidates",
      "availableCandidates",
      "matchedCandidates",
      "dislikedCandidates"
    ]),
    ...mapGetters("election", ["activeElection", "hasElectionProgress"]),
    matchableCandidates() {
      return this.availableCandidates.length !== 0
        ? this.availableCandidates
        : this.dislikedCandidates;
    },
    sortableCandidates() {
      if (this.showAllCandidates) {
        return this.candidates;
      }
      return this.matchableCandidates;
    },
    defaultSortBy() {
      if (this.showAllCandidates) {
        return constants.SORT_LASTNAME;
      }
      return constants.SORT_RANDOM;
    }
  },
  watch: {
    candidateIds() {
      this.updateQueue();
    },
    showAllCandidates(value) {
      if (value) {
        this.setDefaultSortByViewState();
      }
    }
  },
  methods: {
    ...mapActions(["store", "get"]),
    ...mapActions("election", ["updateElectionProgression"]),
    ...mapActions("user", ["updateProgression"]),
    ...mapActions("candidate", ["matchCandidate"]),
    updateQueue() {
      const candidateIds = this.candidateIds.slice();
      let currentCandidateId = this.viewState.candidateId || 0;
      if (currentCandidateId <= 0) {
        this.currentCandidateQueue = candidateIds;
        return;
      }

      let oldIndex = candidateIds.indexOf(currentCandidateId);
      if (oldIndex >= 0) {
        candidateIds.splice(0, oldIndex);
      }
      this.currentCandidateQueue = candidateIds;
    },
    onDragMove(e) {
      let type = this.getMatchType(e.throwDirection);
      let confidence = e.throwOutConfidence;
      this.dragBus.$emit("dragmove", {
        dragType: type,
        confidence: confidence
      });
    },
    onDragEnd() {
      this.dragBus.$emit("dragend");
    },
    async onThrowout({ throwDirection }) {
      let type = this.getMatchType(throwDirection);
      await this.onMatch(type);
    },
    async onMatch(type) {
      this.setHasActiveFilter();
      let candidateId = this.currentCandidateQueue.shift();
      this.viewState.candidateId = null;
      await this.saveViewState();
      await this.matchCandidate({
        candidateId,
        type
      });

      this.markElectionSeen();
      this.unlockAchievement();
      if (this.currentCandidateQueue.length > 0) {
        this.onCandidateChanged(this.currentCandidateQueue[0]);
      }
    },
    getMatchType(throwDirection) {
      switch (throwDirection) {
        case VueSwing.Direction.UP:
          return MatchType.Love;
        case VueSwing.Direction.RIGHT:
          return MatchType.Like;
        case VueSwing.Direction.LEFT:
          return MatchType.Dislike;
        default:
          return "";
      }
    },
    resetCandidateList() {
      // force sort to re-emit new list
      this.viewState.candidateId = null;
      this.saveViewState();
      this.$refs.sorting.sort();
    },
    resetFilter() {
      /**
       * @type {SortingButtons}
       */
      let sorting = this.$refs["sorting"];
      sorting.checkedParties = [];
      sorting.sort();
      this.setHasActiveFilter();
    },
    setHasActiveFilter() {
      this.hasActiveFilter =
        this.viewState &&
        this.viewState.filter &&
        this.viewState.filter.checkedParties &&
        this.viewState.filter.checkedParties.length > 0;
    },
    unlockAchievement() {
      if (this.availableCandidates.length !== 0) {
        return;
      }
      if (!this.hasMatchedCandidates) {
        this.showAchievement = true;
        this.hasMatchedCandidates = true;
        this.updateProgression({
          key: ProgressStepId.Match,
          data: { state: ProgressState.Completed }
        });
      }
    },
    markElectionSeen() {
      if (this.electionSeen) {
        return;
      }
      this.electionSeen = true;
      this.updateElectionProgression({
        electionId: this.activeElection.id,
        key: ElectionProgressStepId.Match,
        data: { state: ProgressState.Completed }
      });
    },
    onCandidateChanged(candidateId) {
      this.viewState.candidateId = candidateId;
      this.saveViewState();
    },
    onFilterChanged(filter) {
      this.viewState.candidateId = null;
      this.viewState.filter = filter;
      this.saveViewState();
      this.updateQueue();
    },
    onAllCandidatesScroll(scrollY) {
      this.viewState.allCandidatesScroll = scrollY;
      this.saveViewState();
    },
    toggleAllCandidatesView() {
      this.showAllCandidates = !this.showAllCandidates;
      this.viewState.showAllCandidates = this.showAllCandidates;
      this.setDefaultSortByViewState();
      this.saveViewState();

      this.$nextTick(() => {
        this.$refs.sorting.sort();
      });
    },
    setDefaultSortByViewState() {
      if (this.viewState.filter == null) {
        this.viewState.filter = {};
      }
      if (this.viewState.filter.sorting == null) {
        this.viewState.filter.sorting = { sortBy: constants.SORT_RANDOM };
      }
      if (
        this.viewState.filter.sorting.sortBy === constants.SORT_RANDOM ||
        this.viewState.filter.sorting.sortBy === constants.SORT_LASTNAME
      ) {
        this.viewState.filter.sorting.sortBy = this.defaultSortBy;
      }
    },
    updateSortedCandidates(event) {
      if (this.showAllCandidates) {
        this.allCandidates = event;
      } else {
        this.candidateIds = event;
      }
    }
  }
};
</script>
<style scoped>
@media (max-width: 380px), (max-height: 600px) {
  .top-spacer {
    height: 1rem;
    flex: 0 0 1rem;
  }
}
</style>
